import { _axios, HttpClient } from './service'
import { IBudgetItem, IBudgetCategory, IBudgetFilters, IBudgetItemsResponse, IBudgetCategoriesResponse } from '../views/budget/types/budget'
import { IBenchmarkFilters } from '../views/budget/types/studyBudget'
import { IPagination } from '../types/pagination'
import { AxiosResponse } from 'axios'

// Interface for BudgetProjectSerializer response
export interface IBudgetProjectResponse {
  id: string | number
  name: string
  description?: string
  status: 'draft' | 'accepted' | 'declined'
  project_type: string
  total_budget: number
  budget_used: number
  data_completeness_percentage: number
  remaining_budget: number
  start_date?: string
  end_date?: string
  department?: {
    id: number
    name: string
  }
  project_manager?: {
    id: number
    user: {
      first_name: string
      last_name: string
    }
  }
  team_members?: Array<{
    id: number
    user: {
      first_name: string
      last_name: string
    }
  }>
  created_at: string
  updated_at: string
  created_by?: {
    id: number
    user: {
      first_name: string
      last_name: string
    }
  }
}

// Add interface for project-level budget data (ProjectBudgetSerializer)
export interface IProjectBudget {
  id: string | number
  project_id: string | number
  name: string
  client: string // API returns 'client' field, not 'project_client'
  client_name?: string // Human-readable client name (serializer source='client.name'); present on API responses
  project_type: 'sponsor' | 'service_provider' // CRITICAL: Add missing project_type field
  total_budget: number
  allocated_budget: number
  spent_amount: number
  remaining_budget: number
  data_completeness_percentage: number
  status: 'draft' | 'accepted' | 'declined'
  health: 'on_track' | 'at_risk' | 'over_budget' | 'completed'
  start_date: string
  end_date: string
  last_updated: string
  created_at: string
  responsible_person: string
  department: string
  therapeutic_area?: string // Using therapeutic_area field from API
  study_type?: string // Study type field from API
  study_phase?: string // Study phase field from API
  priority: 'high' | 'medium' | 'low'
  budget_positions?: any[] // JSON field from backend containing budget position data
  external_positions?: any[] // JSON field from backend containing external position data
  created_by?: {
    id: number
    full_name: string
    first_name: string
    last_name: string
    email: string
    photo?: string
    color?: string
    username: string
  } // Created by user info from EmployeeShortSerializer
  can_edit?: boolean // Permission to edit budget
  can_delete?: boolean // Permission to delete budget
  source_budget?: number | null // If set, this budget is an amendment of the referenced budget
  source_budget_name?: string | null // Name of the amended budget (null for non-amendments)
  has_amendments?: boolean // True when this budget already has an amendment (cannot be amended again)
  // Study Budgets / Orders redesign (issue #19)
  updated_at?: string // Last edited
  accepted_at?: string | null // Manually-set acceptance date
  amendment_delta?: number | null // Amendment total minus source total (can be negative)
  version_number?: number | null // 0-based index within the study family
  version_label?: string | null // "Initial Offer" / amendment name
  version_count?: number | null // Number of versions in the family
}

export interface IProjectBudgetsResponse extends IPagination<IProjectBudget[]> {
  results: IProjectBudget[]
}

export const budgetApi = {
  // Project-Level Budget Management (for Study Budgeting interface)
  getBudgets: async (filters?: IBudgetFilters): Promise<IProjectBudgetsResponse> => {
    const response = await _axios.get('/api/budget-module/projects/', { params: filters })
    return response.data
  },

  /**
   * Fetch EVERY page and return the combined budgets (getBudgets returns only page 1,
   * and the backend caps page_size at 5). Use where a component needs the full set
   * without Redux — the Orders list and the amendment source picker (issue #19 G1/O8).
   * @param filters - same filters as getBudgets; the page cursor is managed internally
   * @returns all matching budgets across every page
   */
  getAllBudgets: async (filters?: IBudgetFilters): Promise<IProjectBudget[]> => {
    const first = await budgetApi.getBudgets(filters)
    const all = [...first.results]
    for (let page = 2; page <= first.total_pages; page++) {
      const next = await budgetApi.getBudgets({ ...filters, page } as any)
      all.push(...next.results)
    }
    return all
  },

  getBudget: async (budgetId: string | number): Promise<IProjectBudget> => {
    const response = await _axios.get(`/api/budget-module/projects/${budgetId}/`)
    return response.data
  },

  /**
   * Fetch the full amendment family for any member id: the true root budget plus
   * every descendant amendment (at any chain depth). Pass any family member.
   * Uses the list endpoint's ?source_budget_id= filter (a member id, not just a root).
   */
  getBudgetVersions: async (memberId: string | number): Promise<IProjectBudget[]> => {
    const response = await _axios.get('/api/budget-module/projects/', {
      params: { source_budget_id: memberId },
    })
    return response.data.results || []
  },

  createBudget: async (data: Partial<IProjectBudget>): Promise<IProjectBudget> => {
    const response = await _axios.post('/api/budget-module/projects/', data)
    return response.data
  },

  updateBudget: async (budgetId: string | number, data: Partial<IProjectBudget>): Promise<IProjectBudget> => {
    const response = await _axios.put(`/api/budget-module/projects/${budgetId}/`, data)
    return response.data
  },

  updateBudgetStatus: async (budgetId: string | number, status: string): Promise<IProjectBudget> => {
    const response = await _axios.post(`/api/budget-module/projects/${budgetId}/change-status/`, { status })
    return response.data
  },

  /**
   * Accept budget with completion validation
   *
   * Calls backend accept-budget endpoint which validates 100% completion.
   * Direct field access - no defensive programming.
   *
   * @param budgetId - Budget ID to accept
   * @returns Updated budget project data
   * @throws API error if validation fails or budget not found
   *
   * @example
   * try {
   *   const updatedBudget = await budgetApi.acceptBudget(123);
   *   console.log('Budget accepted:', updatedBudget.status);
   * } catch (error) {
   *   console.error('Failed to accept:', error.response.data);
   * }
   */
  acceptBudget: async (budgetId: string | number): Promise<IProjectBudget> => {
    const response = await _axios.post(`/api/budget-module/projects/${budgetId}/accept-budget/`)
    return response.data
  },

  deleteBudget: async (budgetId: string | number): Promise<void> => {
    await _axios.delete(`/api/budget-module/projects/${budgetId}/`)
  },

  // Budget Categories
  getBudgetCategories: async (projectId: string | number, filters?: IBudgetFilters): Promise<IBudgetCategoriesResponse> => {
    const response = await _axios.get(`/api/budget-module/projects/${projectId}/categories/`, { params: filters })
    return response.data
  },

  createBudgetCategory: async (projectId: string | number, data: Partial<IBudgetCategory>): Promise<IBudgetCategory> => {
    const response = await _axios.post(`/api/budget-module/projects/${projectId}/categories/`, data)
    return response.data
  },

  updateBudgetCategory: async (projectId: string | number, categoryId: string | number, data: Partial<IBudgetCategory>): Promise<IBudgetCategory> => {
    const response = await _axios.put(`/api/budget-module/projects/${projectId}/categories/${categoryId}/`, data)
    return response.data
  },

  deleteBudgetCategory: async (projectId: string | number, categoryId: string | number): Promise<void> => {
    await _axios.delete(`/api/budget-module/projects/${projectId}/categories/${categoryId}/`)
  },

  // Budget Items
  getBudgetItems: async (projectId: string | number, filters?: IBudgetFilters): Promise<IBudgetItemsResponse> => {
    const response = await _axios.get(`/api/budget-module/projects/${projectId}/items/`, { params: filters })
    return response.data
  },

  createBudgetItem: async (projectId: string | number, data: Partial<IBudgetItem>): Promise<IBudgetItem> => {
    const response = await _axios.post(`/api/budget-module/projects/${projectId}/items/`, data)
    return response.data
  },

  updateBudgetItem: async (projectId: string | number, itemId: string | number, data: Partial<IBudgetItem>): Promise<IBudgetItem> => {
    const response = await _axios.put(`/api/budget-module/projects/${projectId}/items/${itemId}/`, data)
    return response.data
  },

  deleteBudgetItem: async (projectId: string | number, itemId: string | number): Promise<void> => {
    await _axios.delete(`/api/budget-module/projects/${projectId}/items/${itemId}/`)
  },

  // Global Budget Management (for standalone budget module)
  getAllBudgetItems: async (filters?: IBudgetFilters): Promise<IBudgetItemsResponse> => {
    const response = await _axios.get('/api/budget-module/items/', { params: filters })
    return response.data
  },

  getAllBudgetCategories: async (filters?: IBudgetFilters): Promise<IBudgetCategoriesResponse> => {
    const response = await _axios.get('/api/budget-module/categories/', { params: filters })
    return response.data
  },

  // Budget Analytics
  getBudgetSummary: async (projectId?: string | number) => {
    const url = projectId ? `/api/budget-module/projects/${projectId}/summary/` : '/api/budget-module/summary/'
    const response = await _axios.get(url)
    return response.data
  },

  getBudgetAnalytics: async (projectId?: string | number, analyticsType?: string) => {
    const url = projectId ? `/api/budget-module/projects/${projectId}/analytics/` : '/api/budget-module/analytics/'
    const response = await _axios.get(url, { params: { type: analyticsType } })
    return response.data
  },

  // Position Updates - Unified bulk operation
  updatePositionsBulk: async (
    budgetId: string | number,
    data: {
      internal_positions?: Array<{ position_id: string; [key: string]: any }>,
      external_positions?: Array<{ position_id: string; [key: string]: any }>
    }
  ): Promise<any> => {
    const response = await _axios.patch(`/api/budget-module/projects/${budgetId}/positions/`, data)
    return response.data
  },

  /**
   * Export budget positions to CSV
   *
   * Downloads a CSV file containing all budget positions with:
   * - Offer position, department, phase, timeline dates
   * - Unit, quantity, unit hours, unit price
   * - Total hours, total costs
   *
   * @param budgetId - Budget project ID to export
   * @returns Promise that resolves when download completes
   *
   * @example
   * await budgetApi.exportBudget(123);
   */
  exportBudget: async (budgetId: string | number): Promise<Blob | AxiosResponse<Blob>> => {
    return HttpClient.downloadFile(`/api/budget-module/projects/${budgetId}/export/`)
  },

  /**
   * Export budget as PDF
   *
   * Downloads a PDF file containing:
   * - Study parameters (client, therapeutic area, type, phase, dates)
   * - Study phases with timelines
   * - Calculation figures
   * - Allocation of patients
   * - Direct costs (internal positions) by category
   * - Pass-through costs (external positions)
   * - Cost summary totals
   *
   * @param budgetId - Budget project ID to export
   * @returns Promise that resolves when download completes
   *
   * @example
   * await budgetApi.exportBudgetPDF(123);
   */
  exportBudgetPDF: async (budgetId: string | number): Promise<Blob | AxiosResponse<Blob>> => {
    return HttpClient.downloadFile(`/api/budget-module/projects/${budgetId}/export-pdf/`)
  },

  // ============ REPORT ANALYTICS ENDPOINTS ============

  /**
   * Get margin ranking for a budget
   *
   * Returns the budget's margin percentile compared to all other budgets.
   *
   * @param budgetId - Budget project ID
   * @returns Margin ranking data
   */
  getMarginRanking: async (budgetId: string | number): Promise<IMarginRankingResponse> => {
    const response = await _axios.get(`/api/budget-module/projects/${budgetId}/margin-ranking/`)
    return response.data
  },

  /**
   * Get volume ranking for a budget
   *
   * Returns the budget's volume rank (all time, this year, this month).
   *
   * @param budgetId - Budget project ID
   * @returns Volume ranking data
   */
  getVolumeRanking: async (budgetId: string | number): Promise<IVolumeRankingResponse> => {
    const response = await _axios.get(`/api/budget-module/projects/${budgetId}/volume-ranking/`)
    return response.data
  },

  /**
   * Get utilization forecast for a budget
   *
   * Returns monthly hours by department based on position timelines.
   *
   * @param budgetId - Budget project ID
   * @returns Utilization forecast data
   */
  getUtilizationForecast: async (budgetId: string | number): Promise<IUtilizationForecastResponse> => {
    const response = await _axios.get(`/api/budget-module/projects/${budgetId}/utilization-forecast/`)
    return response.data
  },

  /**
   * Get expected profit margin for a budget
   * Calculates margin using billing rates vs average department cost rates
   *
   * @param budgetId - Budget project ID
   * @returns Expected margin data with department breakdown
   */
  getExpectedMargin: async (budgetId: string | number): Promise<IExpectedMarginResponse> => {
    const response = await _axios.get(`/api/budget-module/projects/${budgetId}/expected-margin/`)
    return response.data
  },

  /**
   * Fetches benchmark data for all positions and categories in a budget.
   * @param budgetId - Budget project ID
   * @param filters - Cohort filters (therapeutic area, study type, sponsor, studies, creation date)
   * @returns Benchmark data: per-position current/matching/average/distribution and per-category three-series
   */
  getBenchmarks: async (budgetId: string | number, filters: IBenchmarkFilters) => {
    const params = new URLSearchParams()
    filters.therapeutic_area.forEach(v => params.append('therapeutic_area', v))
    filters.study_type.forEach(v => params.append('study_type', v))
    filters.sponsor.forEach(v => params.append('sponsor', String(v)))
    filters.studies.forEach(v => params.append('studies', String(v)))
    params.append('creation_date', filters.creation_date)
    const response = await _axios.get(`/api/budget-module/projects/${budgetId}/benchmarks/?${params.toString()}`)
    return response.data
  }
}

// ============ REPORT ANALYTICS TYPES ============

export interface IMarginRankingResponse {
  margin: number
  margin_percentage: number
  percentile: number
  top_percentile: number
  rank: number
  total_including_self: number
  total_budgets_compared: number
  message: string
}

export interface IVolumeRankingResponse {
  total_budget: number
  rankings: {
    all_time: IRankingEntry
    this_year: IRankingEntry
    this_month: IRankingEntry
  }
}

interface IRankingEntry {
  rank: number | null
  total: number
  is_top_5: boolean
  label: string | null
}

export interface IUtilizationForecastResponse {
  departments: {
    [department: string]: {
      months: string[]
      hours: number[]
    }
  }
  months: string[]
  project_start: string
  project_end: string
}

export interface IExpectedMarginResponse {
  expected_margin_percentage: number
  expected_margin: number
  total_revenue: number
  total_cost: number
  department_breakdown: {
    [department: string]: {
      hours: number
      revenue: number
      cost: number
      margin: number
      margin_percentage: number
      billing_rate: number
      cost_rate: number
    }
  }
  department_cost_rates: {
    [department: string]: {
      average_cost_rate: number
      employee_count: number
    }
  }
} 